<script setup lang="ts">
import {computed, onMounted, ref} from 'vue';
import {teamsBackend, type Team} from '@/backend/teams.backend';
import {useAuthStore} from '@/stores';
import {useRouter} from 'vue-router';
import AppPage from '@/components/AppPage.vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import Badge from 'primevue/badge';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import {useToast} from 'primevue/usetoast';

const authStore = useAuthStore();
const router = useRouter();
const teams = ref<Team[]>([]);
const nameFilter = ref('');
const showActiveOnly = ref(true);
const loading = ref(false);
const expandedRows = ref<any[]>([]);
const toast = useToast();
const showCreateDialog = ref(false);
const newTeamName = ref('');

const isAdmin = computed(() => authStore.isAdmin);

const filteredTeams = computed(() => {
  let filtered = teams.value;
  
  // Apply active filter
  if (showActiveOnly.value) {
    filtered = filtered.filter(team => team.isActive);
  }
  
  if (nameFilter.value) {
    const searchLower = nameFilter.value.toLowerCase();
    filtered = filtered.filter(team => 
      team.name.toLowerCase().includes(searchLower)
    );
  }
  
  return filtered;
});

onMounted(async () => {
  await loadData();
});

async function loadData() {
  loading.value = true;
  try {
    teams.value = await teamsBackend.getTeams();
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load data',
      life: 3000
    });
  } finally {
    loading.value = false;
  }
}

function openCreateDialog() {
  newTeamName.value = '';
  showCreateDialog.value = true;
}

async function createTeam() {
  if (!newTeamName.value.trim()) {
    toast.add({
      severity: 'warn',
      summary: 'Validation Error',
      detail: 'Team name is required',
      life: 3000
    });
    return;
  }

  loading.value = true;
  try {
    const createdTeam = await teamsBackend.createTeam({
      name: newTeamName.value,
      isActive: true
    });
    
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Team created successfully',
      life: 3000
    });
    
    showCreateDialog.value = false;
    
    // Navigate to the team's edit page
    await router.push(`/teams/${createdTeam.slug}/edit`);
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to create team',
      life: 3000
    });
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <AppPage title="Teams" :loaded="!loading">
    <div class="mb-4 flex justify-between items-center gap-4">
      <div class="flex gap-4 items-center">
        <InputText 
          v-model="nameFilter" 
          placeholder="Search teams..." 
          class="w-64"
        />
      </div>
      <div v-if="isAdmin">
        <Button 
          label="Create Team" 
          icon="pi pi-plus" 
          @click="openCreateDialog"
          severity="primary"
        />
      </div>
    </div>

    <DataTable 
      :value="filteredTeams" 
      :loading="loading"
      paginator 
      :rows="20"
      dataKey="id"
      sortField="name"
      :sortOrder="1"
      :expandedRows="expandedRows"
    >
      <Column expander style="width: 1rem" />
      
      <Column field="name" header="Team Name" sortable>
        <template #body="{ data }">
          <div :class="['font-semibold', { 'text-gray-500': !data.isActive }]">
            <span v-if="!data.isActive" class="text-gray-400">[deactivated] </span>
            <router-link 
              :to="`/teams/${data.slug}`" 
              class="hover:underline cursor-pointer text-primary-600 hover:text-primary-700"
            >
              {{ data.name }}
            </router-link>
          </div>
        </template>
      </Column>
      
      <Column header="Team Lead" sortable sortField="lead.name">
        <template #body="{ data }">
          <span v-if="data.lead">{{ data.lead.name }}</span>
          <span v-else class="text-gray-500">No lead assigned</span>
        </template>
      </Column>
      
      <Column header="Members" sortable sortField="userCount">
        <template #body="{ data }">
          <Badge :value="data.userCount || 0" severity="info" />
        </template>
      </Column>
      
      <Column header="Slack Channel">
        <template #body="{ data }">
          <span v-if="data.slackChannel" class="font-mono text-sm">{{ data.slackChannel }}</span>
          <span v-else class="text-gray-500">-</span>
        </template>
      </Column>

      <template #expansion="{ data }">
        <div class="px-4 py-3">
          <div v-if="data.users && data.users.length > 0" class="flex flex-wrap gap-2">
            <div 
              v-for="user in data.users" 
              :key="user.id"
              class="inline-flex items-center gap-2 px-3 py-2 bg-surface-100 dark:bg-surface-700 rounded-full"
            >
              <span class="font-medium">{{ user.name }}</span>
            </div>
          </div>
          <div v-else class="text-gray-500 italic">
            No team members assigned
          </div>
        </div>
      </template>
    </DataTable>

    <!-- Create Team Dialog -->
    <Dialog 
      v-model:visible="showCreateDialog"
      modal
      header="Create New Team"
      class="w-96"
    >
      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium mb-2">Team Name *</label>
          <InputText 
            v-model="newTeamName" 
            placeholder="Enter team name"
            class="w-full"
            autofocus
            @keyup.enter="createTeam"
          />
        </div>
      </div>

      <template #footer>
        <div class="flex gap-2 justify-end">
          <Button 
            label="Cancel" 
            severity="secondary" 
            @click="showCreateDialog = false"
            :disabled="loading"
          />
          <Button 
            label="Create" 
            @click="createTeam"
            :loading="loading"
            :disabled="!newTeamName.trim()"
          />
        </div>
      </template>
    </Dialog>
  </AppPage>
</template>
